import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Alert, Card, Group, Text, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useLeadRowRepository, useLeadVersionRepository } from '@ayocore/erp/crm/client/rpc';
import type { CreateLeadRow, UpdateLeadRow } from '@ayocore/erp/crm/schemas/lead-row.schema';
import {
  EditableGrid,
  MoneyCell,
  NumberCell,
} from '@ayocore/platform/client/components/EditableGrid';
import type { EditableGridColumn } from '@ayocore/platform/client/components/EditableGrid';
import { createProductSuggestionColumn } from '@ayocore/catalog/client/components/EditableGridColumns';
import type { ProductSource } from '@ayocore/catalog/client/components/EditableGridColumns';
import { LeadRowQuotesBlock } from './LeadRowQuotesBlock';

type LeadRowsGridRow = ProductSource & {
  id?: number;
  product_name?: string | null;
  quantity?: number | null;
  price?: number | null;
  amount?: number | null;
  discount?: number | null;
  total?: number | null;
  sort_order?: number | null;
};

type LeadRowsGridColumnKey = 'product' | 'quantity' | 'price' | 'amount';
/** Патч строки: UpdateLeadRow, при этом product_id/product_name могут быть null (колонка «Товар»). */
type LeadRowsGridPatch = Omit<Partial<UpdateLeadRow>, 'product_id' | 'product_name'> & {
  product_id?: number | null;
  product_name?: string | null;
  product?: null;
};

type LeadRowsTotals = {
  amount: number;
  discount: number;
  total: number;
};

const EMPTY_TOTALS: LeadRowsTotals = {
  amount: 0,
  discount: 0,
  total: 0,
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function calculateRowAmount(row: Pick<LeadRowsGridRow, 'quantity' | 'price'>): number {
  return roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0));
}

function calculateRowsTotals(rows: LeadRowsGridRow[]): LeadRowsTotals {
  return rows.reduce<LeadRowsTotals>(
    (acc, row) => {
      const amount = calculateRowAmount(row);
      const discount = Number(row.discount ?? 0);
      const total = Number(row.total ?? amount - discount);
      acc.amount += amount;
      acc.discount += discount;
      acc.total += total;
      return acc;
    },
    { ...EMPTY_TOTALS }
  );
}

function calculateNewSortOrder(rows: LeadRowsGridRow[], newIndex: number): number {
  const STEP = 1000;
  const BASE = 1000;

  const rawPrev = newIndex > 0 ? (rows[newIndex - 1]?.sort_order ?? null) : null;
  const rawNext = newIndex < rows.length - 1 ? (rows[newIndex + 1]?.sort_order ?? null) : null;

  const prev = rawPrev != null && Number(rawPrev) !== 0 ? Number(rawPrev) : null;
  const next = rawNext != null && Number(rawNext) !== 0 ? Number(rawNext) : null;

  if (prev != null && next != null) {
    return prev === next ? prev + STEP : (prev + next) / 2;
  }
  if (prev != null) {
    return prev + STEP;
  }
  if (next != null) {
    return next > 0 ? next / 2 : BASE;
  }
  return BASE;
}

/**
 * LeadRowsPanel — строки лида на базе EditableGrid.
 */
export function LeadRowsPanel({ leadId }: { leadId: number }) {
  const leadVersionRepo = useLeadVersionRepository();
  const leadRowRepo = useLeadRowRepository();

  const leadVersionRepoRef = useRef(leadVersionRepo);
  const leadRowRepoRef = useRef(leadRowRepo);
  const rowsCacheRef = useRef<LeadRowsGridRow[]>([]);

  const [version, setVersion] = useState<{ id: number; version: string } | null>(null);
  const [totals, setTotals] = useState<LeadRowsTotals>(EMPTY_TOTALS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    leadVersionRepoRef.current = leadVersionRepo;
  }, [leadVersionRepo]);

  useEffect(() => {
    leadRowRepoRef.current = leadRowRepo;
  }, [leadRowRepo]);

  const columns = useMemo<
    EditableGridColumn<LeadRowsGridRow, LeadRowsGridPatch, LeadRowsGridColumnKey>[]
  >(
    () => [
      createProductSuggestionColumn<LeadRowsGridRow>(),
      NumberCell<LeadRowsGridRow, LeadRowsGridPatch, LeadRowsGridColumnKey>({
        key: 'quantity',
        title: 'Кол-во',
        width: 100,
        fallback: 1,
        precision: 0,
        minValue: 0,
      }),
      MoneyCell<LeadRowsGridRow, LeadRowsGridPatch, LeadRowsGridColumnKey>({
        key: 'price',
        title: 'Цена',
        width: 120,
        fallback: 0,
        precision: 2,
        minValue: 0,
      }),
      MoneyCell<LeadRowsGridRow, LeadRowsGridPatch, LeadRowsGridColumnKey>({
        key: 'amount',
        title: 'Итого',
        width: 140,
        value: row => roundMoney(Number(row.quantity ?? 0) * Number(row.price ?? 0)),
        persist: true,
        editable: false,
      }),
    ],
    []
  );

  const loadRows = useCallback(async (): Promise<LeadRowsGridRow[]> => {
    setError(null);
    try {
      const activeVersion = await leadVersionRepoRef.current.getActive(leadId);

      if (!activeVersion) {
        setVersion(null);
        rowsCacheRef.current = [];
        setTotals(EMPTY_TOTALS);
        return [];
      }

      setVersion({ id: activeVersion.id, version: activeVersion.version });
      const rows = (await leadRowRepoRef.current
        .filterByLeadId(leadId, activeVersion.id)
        .fetch()) as LeadRowsGridRow[];
      const normalized = rows ?? [];
      rowsCacheRef.current = normalized;
      setTotals(calculateRowsTotals(normalized));
      return normalized;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      rowsCacheRef.current = [];
      setTotals(EMPTY_TOTALS);
      return [];
    }
  }, [leadId]);

  const createRow = useCallback(
    async (payload: CreateLeadRow): Promise<LeadRowsGridRow> => {
      const activeVersion = await leadVersionRepoRef.current.getActiveOrCreate(leadId);
      setVersion({ id: activeVersion.id, version: activeVersion.version });

      const quantity = Number(payload.quantity ?? 0);
      const price = Number(payload.price ?? 0);
      const normalizedPayload: CreateLeadRow = {
        ...payload,
        lead_id: leadId,
        lead_version_id: activeVersion.id,
        product_name: payload.product_name?.trim() || 'Новая позиция',
        quantity,
        price,
        amount: calculateRowAmount({ quantity, price }),
        vat: Number(payload.vat ?? 0),
        metadata: payload.metadata ?? null,
      };

      const created = (await leadRowRepoRef.current.create(normalizedPayload)) as LeadRowsGridRow;
      const nextRows = [...rowsCacheRef.current, created];
      rowsCacheRef.current = nextRows;
      setTotals(calculateRowsTotals(nextRows));
      return created;
    },
    [leadId]
  );

  const updateRow = useCallback(async (rowId: number, patch: LeadRowsGridPatch): Promise<void> => {
    await leadRowRepoRef.current.update(rowId, patch as UpdateLeadRow);
    const nextRows = rowsCacheRef.current.map(row =>
      row.id === rowId ? { ...row, ...patch } : row
    );
    rowsCacheRef.current = nextRows;
    setTotals(calculateRowsTotals(nextRows));
  }, []);

  const deleteRow = useCallback(async (rowId: number): Promise<void> => {
    await leadRowRepoRef.current.deleteById(rowId);
    const nextRows = rowsCacheRef.current.filter(row => row.id !== rowId);
    rowsCacheRef.current = nextRows;
    setTotals(calculateRowsTotals(nextRows));
  }, []);

  const defaults = useMemo<CreateLeadRow>(
    () => ({
      lead_id: leadId,
      lead_version_id: 0,
      product_id: null,
      product_name: 'Новая позиция',
      product_category: null,
      product_brand: null,
      product_supplier: null,
      quantity: 1,
      price: 0,
      amount: 0,
      vat: 0,
      purchase_price: null,
      metadata: null,
    }),
    [leadId]
  );

  const dataSource = useMemo(
    () => ({
      loadRows,
      createRow,
      updateRow,
      deleteRow,
      defaults,
    }),
    [createRow, defaults, deleteRow, loadRows, updateRow]
  );

  const expandableConfig = useMemo(
    () => ({
      mode: 'single' as const,
      renderExpandedRow: ({ rowId }: { rowId: number }) => <LeadRowQuotesBlock leadRowId={rowId} />,
    }),
    []
  );

  const sortableConfig = useMemo(
    () => ({
      onReorder: async ({
        activeRowId,
        oldIndex,
        newIndex,
        rows,
        actions,
      }: {
        activeRowId: number;
        oldIndex: number;
        newIndex: number;
        rows: LeadRowsGridRow[];
        actions: {
          replaceRows: (nextRows: LeadRowsGridRow[]) => void;
        };
      }) => {
        const ordered = [...rows] as LeadRowsGridRow[];
        const [moved] = ordered.splice(oldIndex, 1);
        if (!moved) return;
        ordered.splice(newIndex, 0, moved);

        const nextOrder = calculateNewSortOrder(ordered, newIndex);
        const optimisticRows = ordered.map(row =>
          row.id === activeRowId ? { ...row, sort_order: nextOrder } : row
        );

        const previousRows = [...rowsCacheRef.current];
        rowsCacheRef.current = optimisticRows;
        actions.replaceRows(optimisticRows);

        try {
          await leadRowRepoRef.current.setRowPosition(activeRowId, nextOrder);
        } catch (error) {
          rowsCacheRef.current = previousRows;
          actions.replaceRows(previousRows);
          throw error;
        }
      },
    }),
    []
  );

  return (
    <Card padding='xs' shadow='sm'>
      <RowsHeader version={version} />

      {error && (
        <Alert color='red' title='Ошибка' mb='xs' maw='100%'>
          <Text size='xs'>{error}</Text>
        </Alert>
      )}

      <EditableGrid
        columns={columns}
        getRowId={row => row.id}
        dataSource={dataSource}
        loadingText='Загрузка строк лида...'
        emptyText='Нет строк в активной версии'
        tableMinWidth={620}
        actionColumnWidth={48}
        appendEmptyRow
        expandable={expandableConfig}
        sortable={sortableConfig}
        renderRowActions={({ rowId, state, actions }) => (
          <Tooltip label='Удалить строку'>
            <ActionIcon
              size='xs'
              color='red'
              variant='subtle'
              disabled={state.rowSaving || state.creatingRow}
              onClick={() => {
                if (!confirm('Удалить эту строку?')) return;
                void actions.deleteRow(rowId);
              }}
            >
              <IconTrash size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      />

      <RowsTotals totals={totals} />
    </Card>
  );
}

const RowsHeader = React.memo(function RowsHeader({
  version,
}: {
  version: { id: number; version: string } | null;
}) {
  return (
    <Group justify='space-between' mb='xs'>
      <Text fw={600} size='xs'>
        Позиции запроса
      </Text>
      {version && (
        <Text size='xs' c='dimmed'>
          {version.version}
        </Text>
      )}
    </Group>
  );
});

const RowsTotals = React.memo(function RowsTotals({ totals }: { totals: LeadRowsTotals }) {
  return (
    <Group justify='flex-end' mt='xs'>
      <Text size='xs' fw={700}>
        Итого: {totals.total.toLocaleString()}
      </Text>
    </Group>
  );
});

export default LeadRowsPanel;
